import React, { useEffect, useState } from "react";
import {
  Card,
  List,
  Button,
  Input,
  Modal,
  Form,
  Popconfirm,
  Typography,
  Tag,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";

const { Title, Text } = Typography;

function DropdownAttributes() {
  const [dropdownAttributes, setDropdownAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addValueModal, setAddValueModal] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    getDropdownAttributes();
  }, []);

  async function getDropdownAttributes() {
    setLoading(true);
    try {
      // Direct API call to bypass caching issues
      const token = "Bearer " + localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}v1/plan-attributes/dropdown`,
        {
          headers: {
            Authorization: token,
          },
        }
      );
      const apiResponse = await response.json();

      if (apiResponse.success) {
        setDropdownAttributes(apiResponse.data);
      } else {
        openNotificationError("bottomRight", "Failed to fetch dropdown attributes");
      }
    } catch (error) {
      console.error("Error fetching dropdown attributes:", error);
      openNotificationError("bottomRight", "Error fetching dropdown attributes");
    } finally {
      setLoading(false);
    }
  }

  async function addDropdownValue(attributeId, value) {
    try {
      // Direct API call to bypass caching issues
      const token = "Bearer " + localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}v1/plan-attributes/dropdown-values`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({
            planAttributeId: attributeId,
            value: value,
            displayOrder: 0,
          }),
        }
      );
      const apiResponse = await response.json();

      if (apiResponse.success) {
        openNotificationSuccess("bottomRight", "Dropdown value added successfully");
        getDropdownAttributes();
        return true;
      } else {
        openNotificationError("bottomRight", apiResponse.message || "Failed to add dropdown value");
        return false;
      }
    } catch (error) {
      console.error("Error adding dropdown value:", error);
      openNotificationError("bottomRight", "Error adding dropdown value");
      return false;
    }
  }

  async function deleteDropdownValue(valueId) {
    try {
      // Direct API call to bypass caching issues
      const token = "Bearer " + localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}v1/plan-attributes/dropdown-values/${valueId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: token,
          },
        }
      );
      const apiResponse = await response.json();

      if (apiResponse.success) {
        openNotificationSuccess("bottomRight", "Dropdown value deleted successfully");
        getDropdownAttributes();
      } else {
        openNotificationError("bottomRight", apiResponse.message || "Failed to delete dropdown value");
      }
    } catch (error) {
      console.error("Error deleting dropdown value:", error);
      openNotificationError("bottomRight", "Error deleting dropdown value");
    }
  }

  function handleAddValue(attribute) {
    setSelectedAttribute(attribute);
    setAddValueModal(true);
    form.resetFields();
  }

  async function onFinishAddValue(values) {
    const success = await addDropdownValue(selectedAttribute.id, values.value);
    if (success) {
      setAddValueModal(false);
      setSelectedAttribute(null);
    }
  }

  function handleCancelAddValue() {
    setAddValueModal(false);
    setSelectedAttribute(null);
    form.resetFields();
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2}>Dropdown Attributes Management</Title>
        <Text type="secondary">
          Manage dropdown values for plan attributes. You can add or remove values for each dropdown attribute.
        </Text>
      </div>

      <div className="grid gap-4 max-w-4xl">
          {dropdownAttributes.map((attribute) => (
            <Card
              key={attribute.id}
              size="small"
              title={
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-base font-medium">{attribute.name}</span>
                    <Tag color="blue" className="ml-2" size="small">
                      {attribute.dropdownOptions?.length || 0} values
                    </Tag>
                  </div>
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddValue(attribute)}
                  >
                    Add Value
                  </Button>
                </div>
              }
              className="shadow-sm"
            >
              {attribute.dropdownOptions && attribute.dropdownOptions.length > 0 ? (
                <List
                  dataSource={attribute.dropdownOptions}
                  renderItem={(option) => (
                    <List.Item
                      actions={[
                        <Popconfirm
                          key="delete"
                          title="Are you sure you want to delete this value?"
                          description="This action cannot be undone. If this value is being used in plans, it will be archived instead."
                          onConfirm={() => deleteDropdownValue(option.id)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                          >
                            Delete
                          </Button>
                        </Popconfirm>,
                      ]}
                    >
                      <List.Item.Meta
                        title={option.value}
                        description={`Display Order: ${option.displayOrder || 0}`}
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Text type="secondary">No dropdown values configured</Text>
                  <br />
                  <Text type="secondary">Click "Add Value" to create the first option</Text>
                </div>
              )}
            </Card>
          ))}

          {dropdownAttributes.length === 0 && !loading && (
            <Card className="text-center py-8" size="small">
              <Text type="secondary" className="text-base">
                No dropdown attributes found
              </Text>
              <br />
              <Text type="secondary">
                Create dropdown attributes in the Plan Attributes settings first
              </Text>
            </Card>
          )}
        </div>

      {/* Add Value Modal */}
      <Modal
        title={`Add Value to "${selectedAttribute?.name}"`}
        open={addValueModal}
        onCancel={handleCancelAddValue}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinishAddValue}
          requiredMark={false}
        >
          <Form.Item
            label="Value"
            name="value"
            rules={[
              {
                required: true,
                message: "Please enter a value",
              },
              {
                min: 1,
                message: "Value must be at least 1 character",
              },
              {
                max: 100,
                message: "Value must be less than 100 characters",
              },
            ]}
          >
            <Input
              placeholder="Enter dropdown value"
              autoFocus
            />
          </Form.Item>

          <Form.Item className="mb-0">
            <div className="flex justify-end gap-2">
              <Button onClick={handleCancelAddValue}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Add Value
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}


export default DropdownAttributes;